import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useCartStore } from '@astro/store'
import { useToast } from '../context/ToastContext.jsx'
import SimilarProducts from '../components/sections/SimilarProducts'
import DeliveryEstimate, { isPincode } from '../components/common/DeliveryEstimate.jsx'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { currencyFormatter } from '../components/sections/helper'
import { useGeneralSettings } from '../hooks/useGeneralSettings'

// Add-on groups come from the admin (`product.variations` → each group's
// `options`). An option's `price` (rupees) is an ADD-ON on top of total_price —
// 0 means included. The selected option ids travel to the cart/checkout APIs as
// `variation_id`, and the server charges total_price + each option's price, so
// this page's live price always matches what's billed. Products without add-ons
// render no dropdowns at all.
const toOptionGroups = (variations = []) =>
  variations
    .filter((v) => v.options?.length > 0)
    .map((v) => ({
      key: String(v.id),
      label: v.name,
      options: v.options.map((o) => ({
        id: o.id,
        value: String(o.id),
        label: o.variation_name || o.value || `Option ${o.id}`,
        price: parseFloat(o.price) || 0,
      })),
    }))

/** { [groupId]: optionId } — the first option of every group. */
const defaultChoices = (groups) =>
  Object.fromEntries(groups.map((g) => [g.key, g.options[0].value]))

/** Choices → CartItemOption[] (prices converted to paise for the cart line). */
const buildCartOptions = (groups, choices) =>
  groups.map((g) => {
    const o = g.options.find((x) => x.value === choices[g.key]) ?? g.options[0]
    return { key: g.key, label: g.label, value: o.value, display: o.label, price: Math.round(o.price * 100), optionId: o.id }
  })

const CLOUD = 'https://res.cloudinary.com/doufbflam/image/upload/f_auto,q_auto,c_limit,w_1600'

/** Strip HTML tags for plain-text preview */
const stripHtml = (html) => (html ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()

/** Fields already rendered elsewhere on this page (gallery, title, pricing,
 * descriptions, …) or internal-only flags — excluded from the generic
 * fallback below so the spec grid doesn't repeat/clutter with them. */
const SPEC_SKIP_KEYS = new Set([
  'id', 'name', 'slug', 'image_url', 'media', 'categories', 'sort_description',
  'long_description', 'specifications', 'reviews', 'origin_country', 'variations',
  'is_wishlisted', 'is_featured', 'is_special', 'is_home', 'is_visible', 'wishlists',
  'created_at', 'updated_at',
  'price', 'product_price', 'discount_rate', 'discount_price', 'total_price', 'final_price',
  'stock', 'sku', 'ratti', 'carat', 'origin_name', 'weight', 'dimensions', 'variation_name',
  'gst_rate', 'gst_amount',
  // Raw internal foreign keys / classifiers with no shopper-facing label on
  // this endpoint (no resolved brand or grade name comes back) — skip rather
  // than print bare ids like "Brand Id: 99".
  'brand_id', 'origin', 'product_grade_id', 'product_type',
])

const labelize = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

/** Non-empty trimmed string, else '' — the API sends empty strings for unset fields. */
const val = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '')

/** wa.me/tel want digits; bare 10-digit Indian numbers get the 91 prefix. */
const toDigits = (raw) => {
  const digits = raw.replace(/\D/g, '')
  return digits.length === 10 ? `91${digits}` : digits
}

const FALLBACK_CONTACT_NUMBER = '919091840899'

export default function ProductDetails() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const add = useCartStore((s) => s.add)
  const setBuyNow = useCartStore((s) => s.setBuyNow)
  const { showToast } = useToast()
  const { settings } = useGeneralSettings()

  // WhatsApp goes to the dedicated whatsapp number, falling back to the main
  // phone; calls go to the phone directly.
  const waNumber = toDigits(val(settings.whatsapp) || val(settings.phone)) || FALLBACK_CONTACT_NUMBER
  const callNumber = toDigits(val(settings.phone) || val(settings.support_phone)) || FALLBACK_CONTACT_NUMBER

  const { data, isLoading } = useQuery({
    queryKey: ['productDetails', slug],
    queryFn: () => api.catalog.bySlug(slug),
  })

  // /product/details/:slug replies with the payload under `product`, not `data`
  const product = data?.product

  // Live cart entry for this product from the persisted astro-cart state
  const cartItem = useCartStore((s) => s.items.find((i) => i.productId === product?.id))
  const setCartQty = useCartStore((s) => s.setQty)
  const setCartOptions = useCartStore((s) => s.setOptions)
  const removeFromCart = useCartStore((s) => s.remove)
  const inCart = Boolean(cartItem)

  // relatedProducts rides along with the details response (keyed "1".."N",
  // not an array — same shape as the base product) — no separate
  // /products/:id/related call needed.
  const similar = Object.values(data?.relatedProducts ?? {})

  // Main image first (is_main), then admin order; fall back to the cover url.
  const gallery = useMemo(() => {
    const media = product?.media ?? []
    const urls = [...media]
      .sort(
        (a, b) =>
          Number(b.custom_properties?.is_main === true) - Number(a.custom_properties?.is_main === true) ||
          (a.order_column ?? 0) - (b.order_column ?? 0),
      )
      .map((m) => m.original_url)
      .filter(Boolean)
    return urls.length > 0 ? urls : [product?.image_url].filter(Boolean)
  }, [product])

  const optionGroups = useMemo(() => toOptionGroups(product?.variations), [product?.variations])

  const category =
    product?.categories?.find((c) => c.parent_id === null)?.name || product?.categories?.[0]?.name || ''
  const hasDiscount = parseFloat(product?.discount_rate) > 0
  const stockCount = parseInt(product?.stock) || 0
  const inStock = stockCount > 0

  // Rating summary — straight off the details response; hidden entirely
  // once there are no reviews yet rather than showing an empty/fake state.
  const reviewCount = data?.reviews?.length ?? product?.reviews?.length ?? 0
  const avgRating = data?.averageRating ?? null

  // Spec grid: known catalog fields, then admin-entered specification rows,
  // then anything else on the product record — so nothing the API actually
  // returns gets silently dropped, and no field renders twice.
  const specs = useMemo(() => {
    if (!product) return []
    const rows = []
    const seen = new Set()
    const push = (label, value) => {
      if (value === null || value === undefined || value === '') return
      const key = String(label).trim().toLowerCase()
      if (seen.has(key)) return
      seen.add(key)
      rows.push([label, typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value])
    }

    push('SKU ID', product.sku)
    push('CATEGORY', category)
    if (parseFloat(product.ratti) > 0) push('RATTI', product.ratti)
    if (parseFloat(product.carat) > 0) push('CARAT', `${product.carat} CARAT`)
    push('ORIGIN', product.origin_name || product.origin_country?.name)
    push('WEIGHT', product.weight)
    push('DIMENSIONS', product.dimensions)
    if (parseFloat(product.gst_rate) > 0) push('GST', `${parseFloat(product.gst_rate)}%`)
    push('AVAILABILITY', inStock ? `IN STOCK (${stockCount})` : 'OUT OF STOCK')

    // Admin-entered specification rows from the CMS (mineral species, color,
    // treatment, …) — the richest source, so these fill in anything the
    // fields above didn't already cover.
    for (const s of product.specifications ?? []) {
      if (s.title) push(s.title, s.description)
    }

    // Whatever's left on the product record that isn't rendered elsewhere on
    // this page and isn't an object/array — keeps this grid complete even as
    // new fields get added upstream (this API is loosely documented).
    for (const [key, value] of Object.entries(product)) {
      if (SPEC_SKIP_KEYS.has(key) || typeof value === 'object') continue
      push(labelize(key), value)
    }

    return rows
  }, [product, category, inStock, stockCount])

  const [activeImage, setActiveImage] = useState(0)
  const [qty, setQty] = useState(1)
  const [pincode, setPincode] = useState('')
  // PIN last submitted with Check — DeliveryEstimate shows its Shiprocket result
  const [checkedPincode, setCheckedPincode] = useState('')
  const [lightbox, setLightbox] = useState(false)
  // Selected option id per add-on group, e.g. { '17': '35' }
  const [choices, setChoices] = useState({})

  useEffect(() => {
    setActiveImage(0)
    setQty(1)
  }, [slug])

  // When the product loads, restore the choices saved on its cart line (if
  // any) so the page shows exactly what the basket will charge. Stored picks
  // the admin has since removed fall back to the group's first option.
  useEffect(() => {
    if (!product?.id) return
    const stored = useCartStore.getState().items.find((i) => i.productId === product.id)?.options ?? []
    const stillOffered = stored.filter((o) =>
      optionGroups.some((g) => g.key === o.key && g.options.some((x) => x.value === o.value))
    )
    setChoices({ ...defaultChoices(optionGroups), ...Object.fromEntries(stillOffered.map((o) => [o.key, o.value])) })
  }, [product?.id, optionGroups])

  // Live selection → per-unit add-on that moves the main price on this page.
  const selectedOptions = buildCartOptions(optionGroups, choices)
  const addOnRupees = selectedOptions.reduce((n, o) => n + o.price, 0) / 100

  // If the item is already in the basket, changing a dropdown re-prices the
  // cart line immediately; before that it only updates local state.
  const selectOption = (key, value) => {
    const next = { ...choices, [key]: value }
    setChoices(next)
    if (inCart) setCartOptions(product.id, buildCartOptions(optionGroups, next))
  }

  if (isLoading) return <DetailsSkeleton />

  if (!product) {
    return (
      <div className="flex flex-col min-h-screen bg-background items-center justify-center px-6 pt-28 pb-20 text-center normal-case select-none">
        <i className="fa-solid fa-gem text-4xl text-primary/20"></i>
        <h1 className="mt-6 text-2xl font-heading text-primary uppercase">Product not found</h1>
        <p className="mt-3 text-sm text-gray-500 font-body max-w-md">
          This item may have been removed or the link is incorrect. Explore the full collection instead.
        </p>
        <Link
          to="/products"
          className="mt-8 inline-flex items-center gap-2 px-8 h-12 rounded-xl bg-primary text-white text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-secondary transition-all"
        >
          <i className="fa-solid fa-arrow-left"></i> Back to Divine Shop
        </Link>
      </div>
    )
  }

  // Cart items store the BASE catalog price in paise (see Cart.jsx formatINR);
  // option add-ons ride separately on `options` so the server-cart sync can
  // refresh the base price without wiping the customization surcharge.
  const pricePaise = Math.round(parseFloat(product.total_price) * 100)

  // Toggles the cart entry: adds when absent, removes when already in the basket
  const toggleCart = () => {
    if (!inStock) return
    if (inCart) {
      removeFromCart(product.id)
      showToast(`${product.name} removed from basket`)
      return
    }
    add({ productId: product.id, title: product.name, price: { amount: pricePaise, currency: 'INR' }, qty, options: selectedOptions })
    showToast(`${product.name} × ${qty} added to basket`)
  }

  // Stepper edits local qty before adding; once in cart it edits the cart qty directly
  const displayQty = cartItem ? cartItem.qty : qty

  // Express purchase: the hot item rides the store's dedicated buy-now slot —
  // never the basket — and checkout opens in buy-now mode via the query param.
  // Both survive the login redirect (persisted store + full-URL returnTo).
  const buyNow = () => {
    if (!inStock) return
    setBuyNow({
      productId: product.id,
      title: product.name,
      price: { amount: pricePaise, currency: 'INR' },
      qty: displayQty,
      options: selectedOptions,
      image: gallery[0],
      category,
    })
    navigate('/checkout?mode=buynow')
  }
  const changeQty = (delta) => {
    const next = Math.min(Math.max(stockCount, 1), Math.max(1, displayQty + delta))
    if (cartItem) setCartQty(product.id, next)
    else setQty(next)
  }

  const checkDelivery = () => {
    if (!isPincode(pincode)) {
      showToast('Enter a valid 6-digit pincode')
      return
    }
    setCheckedPincode(pincode)
  }

  const chevron = (
    <svg className="w-3 h-3 shrink-0 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="m9 18 6-6-6-6"></path>
    </svg>
  )

  return (
    <div className="flex flex-col min-h-screen bg-background font-normal uppercase select-none">
      {/* TOP: BREADCRUMB + GALLERY + DETAILS */}
      <div className="bg-white pt-28 md:pt-32 pb-10 md:pb-14 border-b border-gray-200">
        <div className="container px-6 mx-auto">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-normal uppercase tracking-[0.3em] text-gray-400">
            <Link to="/" className="hover:text-secondary transition-colors">HOME</Link>
            <span className="flex items-center gap-3">
              {chevron}
              <Link to="/products" className="hover:text-secondary transition-colors">DIVINE SHOP</Link>
            </span>
            <span className="flex min-w-0 items-center gap-3">
              {chevron}
              <span className="truncate text-secondary normal-case">{product.name}</span>
            </span>
          </nav>

          <section className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            {/* Product Gallery */}
            <div className="lg:col-span-6 space-y-4">
              <div className="flex gap-4">
                {/* Thumbnails */}
                {gallery.length > 1 && (
                  <div className="hidden sm:flex flex-col gap-3 w-20 shrink-0">
                    {gallery.map((img, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setActiveImage(index)}
                        className={`relative aspect-square rounded-lg overflow-hidden border transition-all duration-300 ${activeImage === index
                          ? 'border-secondary ring-2 ring-secondary/10'
                          : 'border-border hover:border-primary/40'
                          }`}
                      >
                        <img src={img} alt="" className="object-cover w-full h-full" />
                      </button>
                    ))}
                  </div>
                )}

                {/* Main image */}
                <div className="relative flex-1 aspect-square rounded-xl overflow-hidden border border-border/60 bg-white group">
                  <img src={gallery[activeImage] ?? gallery[0]} alt={product.name} className="object-contain w-full h-full transition-all duration-500" />

                  <div className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => setLightbox(true)}
                      className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-md border border-border flex items-center justify-center hover:bg-secondary hover:text-white transition-all shadow-sm"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <polyline points="9 21 3 21 3 15"></polyline>
                        <line x1="21" x2="14" y1="3" y2="10"></line>
                        <line x1="3" x2="10" y1="21" y2="14"></line>
                      </svg>
                    </button>
                  </div>

                  {gallery.length > 1 && (
                    <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-4">
                      <button
                        onClick={() => setActiveImage((i) => (i > 0 ? i - 1 : gallery.length - 1))}
                        className="w-10 h-10 rounded-full bg-white/90 border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-secondary hover:text-white transition-all"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"></path></svg>
                      </button>
                      <button
                        onClick={() => setActiveImage((i) => (i < gallery.length - 1 ? i + 1 : 0))}
                        className="w-10 h-10 rounded-full bg-white/90 border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-secondary hover:text-white transition-all"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6"></path></svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Blessing Badge */}
              <div className="p-3 rounded-xl bg-white border border-gray-200 flex items-center gap-4 hover:border-secondary transition-all">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-secondary border border-amber-100">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path>
                  </svg>
                </div>
                <div>
                  <p className="text-[8px] font-mono uppercase tracking-[0.3em] text-gray-400">Ancient Quality</p>
                  <p className="text-xs text-gray-900 uppercase tracking-wider">Puja &amp; Energy Blessing Completed</p>
                </div>
              </div>
            </div>

            {/* Product Details */}
            <div className="lg:col-span-6 space-y-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  {category && (
                    <span className="bg-gray-900 text-white px-3 py-1 rounded-lg text-[9px] uppercase tracking-widest inline-block">{category}</span>
                  )}
                  {hasDiscount && (
                    <span className="bg-secondary text-white px-3 py-1 rounded-lg text-[9px] uppercase tracking-widest inline-block">{parseFloat(product.discount_rate)}% OFF</span>
                  )}
                </div>
                <h1 className="text-3xl text-gray-900 tracking-tight normal-case font-heading">{product.name}</h1>
                {reviewCount > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-0.5 text-secondary text-xs">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className={`fa-solid fa-star ${i < Math.round(avgRating ?? 0) ? '' : 'opacity-20'}`}></i>
                      ))}
                    </div>
                    <span className="text-[10px] text-gray-500 normal-case tracking-wide">
                      {avgRating != null ? `${avgRating.toFixed(1)} · ` : ''}{reviewCount} review{reviewCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}
                <div className="flex items-baseline gap-4 pt-2 border-t border-dashed border-gray-200">
                  <span className="text-3xl text-red-600 font-medium">{currencyFormatter(parseFloat(product.total_price) + addOnRupees)}</span>
                  {hasDiscount && (
                    <span className="text-base text-gray-400 line-through">{currencyFormatter(parseFloat(product.product_price) + addOnRupees)}</span>
                  )}
                  <span className="text-[10px] text-gray-400 uppercase tracking-widest">
                    Final Price (Tax Included)
                    {addOnRupees > 0 && <span className="block text-secondary normal-case">Incl. ₹{addOnRupees.toLocaleString('en-IN')} add-ons</span>}
                  </span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed normal-case">{stripHtml(product.sort_description)}</p>
              </div>

              {/* Add-ons — controlled; every change re-prices the page (and the cart line when already added) */}
              {optionGroups.length > 0 && (
                <div className="space-y-4 border-t border-dashed border-gray-200 pt-4">
                  {optionGroups.map((g) => (
                    <OptionRow
                      key={g.key}
                      label={g.label}
                      options={g.options}
                      value={choices[g.key] ?? g.options[0].value}
                      onChange={(v) => selectOption(g.key, v)}
                    />
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between text-xs py-1">
                <span className="text-gray-500">Availability</span>
                {inStock ? (
                  <span className="text-green-600 font-medium flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-600"></span> In Stock
                  </span>
                ) : (
                  <span className="text-red-500 font-medium flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500"></span> Out of Stock
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-dashed border-gray-200">
                {/* Quantity */}
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] uppercase tracking-widest text-gray-500">Total Quantity</label>
                  <div className="flex items-center w-full border border-gray-200 rounded-lg p-1 bg-white">
                    <button type="button" onClick={() => changeQty(-1)} className="w-10 h-8 flex items-center justify-center hover:bg-gray-100 rounded transition-colors">-</button>
                    <span className="flex-1 text-center text-sm font-mono font-medium">{displayQty}</span>
                    <button type="button" onClick={() => changeQty(1)} className="w-10 h-8 flex items-center justify-center hover:bg-gray-100 rounded transition-colors">+</button>
                  </div>
                </div>

                {/* Delivery */}
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] uppercase tracking-widest text-gray-500">Check Delivery</label>
                  <div className="flex w-full">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={pincode}
                      onChange={(e) => {
                        setPincode(e.target.value.replace(/\D/g, ''))
                        setCheckedPincode('')
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && checkDelivery()}
                      placeholder="Enter Pincode"
                      className="w-full h-10 border border-gray-200 rounded-l-lg px-4 text-xs outline-none focus:ring-1 focus:ring-secondary"
                    />
                    <button onClick={checkDelivery} className="bg-gray-900 text-white px-4 rounded-r-lg text-[10px] uppercase tracking-widest hover:bg-secondary transition-colors">Check</button>
                  </div>
                  <DeliveryEstimate pincode={checkedPincode} />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-6">
                {/* `w-full sm:flex-1`, not a bare `flex-1`: while the row is
                    `flex-col`, flex-basis:0 governs the HEIGHT and collapses
                    these to text height, overriding h-12. */}
                <button onClick={toggleCart} disabled={!inStock} className="w-full sm:flex-1 h-12 shrink-0 rounded-lg bg-gray-900 text-white hover:bg-secondary transition-all uppercase text-[11px] tracking-[0.2em] whitespace-nowrap shadow-sm flex items-center justify-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-900">
                  {inCart ? 'Remove from Cart' : 'Add to Cart'}
                  {inCart ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18"></path>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" x2="10" y1="11" y2="17"></line>
                      <line x1="14" x2="14" y1="11" y2="17"></line>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path>
                      <path d="M3 6h18"></path>
                      <path d="M16 10a4 4 0 0 1-8 0"></path>
                    </svg>
                  )}
                </button>
                <button onClick={buyNow} disabled={!inStock} className="w-full sm:flex-1 h-12 shrink-0 rounded-lg bg-secondary text-white hover:bg-gray-900 transition-all uppercase text-[11px] tracking-[0.2em] whitespace-nowrap shadow-sm flex items-center justify-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-secondary">
                  Buy Now
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="14" x="2" y="5" rx="2"></rect>
                    <line x1="2" x2="22" y1="10" y2="10"></line>
                  </svg>
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* ITEM DETAIL / SPEC GRID */}
      <section className="container mx-auto px-6 py-12">
        <div className="mb-8 border-b border-gray-200 pb-6">
          <h2 className="text-2xl font-semibold text-gray-900 tracking-tight">ITEM DETAIL: {product.name}</h2>
          {product.long_description ? (
            <div
              className="text-sm text-gray-600 mt-3 leading-relaxed normal-case [&_p]:mb-2"
              dangerouslySetInnerHTML={{ __html: product.long_description }}
            />
          ) : (
            <p className="text-sm text-gray-600 mt-3 leading-relaxed normal-case">{stripHtml(product.sort_description)}</p>
          )}
        </div>

        {specs.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            {specs.map(([label, value]) => (
              <div key={label} className="bg-white p-4">
                <p className="text-[9px] uppercase tracking-widest text-gray-400 font-bold">{label}</p>
                <p className="text-xs font-semibold text-gray-900 mt-1">{value}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 flex gap-3 text-gray-500">
          <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4M12 8h.01"></path></svg>
          <p className="text-[10px] uppercase italic">
            Note: Clinical averages for high-precision synchronization. Items are ritual-purified for 48 hours before shipment.
          </p>
        </div>
      </section>

      {/* SYSTEM RECOMMENDATION */}
      <section className="py-10 bg-gray-50 border-b border-gray-200">
        <div className="container px-6 mx-auto">
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm flex flex-col lg:flex-row items-stretch">
            <div className="lg:w-5/12 relative bg-gray-100 p-3">
              <div className="relative w-full h-80 rounded-xl overflow-hidden">
                <img alt="Gemstone category" src={`${CLOUD}/v1781702788/categories/ovqk1u4cln2gfxqlhawq.webp`} className="object-cover w-full h-full" />
                <span className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm text-gray-900 text-[9px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full border border-gray-200">Gemstone</span>
              </div>
            </div>

            <div className="lg:w-7/12 flex flex-col bg-white overflow-hidden border border-gray-100 rounded-2xl shadow-sm">
              <div className="px-8 pt-8 pb-6 border-b border-gray-50">
                <div className="flex items-center gap-3 mb-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                  </span>
                  <span className="text-[9px] font-bold text-amber-700 uppercase tracking-[0.25em]">System recommendation</span>
                </div>
                <h3 className="text-2xl text-gray-900 font-medium tracking-tight normal-case">Discover the perfect gemstone system</h3>
              </div>

              <div className="px-8 py-8 grid grid-cols-1 md:grid-cols-3 gap-8">
                <FeatureItem title="Personal help" subtitle="Direct chat sync">
                  <circle cx="12" cy="12" r="10" /><circle cx="12" cy="10" r="3" /><path d="M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662" />
                </FeatureItem>
                <FeatureItem title="Best price" subtitle="Exclusive offers">
                  <rect x="3" y="8" width="18" height="4" rx="1" /><path d="M12 8v13" />
                </FeatureItem>
                <FeatureItem title="Safe choice" subtitle="Satisfaction assured">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </FeatureItem>
              </div>

              <div className="px-8 pb-8 flex flex-col sm:flex-row gap-4">
                <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer" className="flex items-center justify-center px-6 py-3.5 flex-1 rounded-xl border border-gray-200 hover:border-green-500 hover:text-green-600 transition-all uppercase tracking-widest text-[10px] font-bold gap-2.5">
                  <i className="fa-brands fa-whatsapp text-base"></i>
                  Chat on WhatsApp
                </a>
                <a href={`tel:+${callNumber}`} className="flex items-center justify-center px-6 py-3.5 flex-1 rounded-xl bg-gray-900 text-white hover:bg-black transition-all uppercase tracking-widest text-[10px] font-bold gap-2.5">
                  <i className="fa-solid fa-phone text-sm"></i>
                  Call Us Today
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KNOWLEDGE FOR SEEKERS */}
      <section className="py-10 md:py-14 bg-white">
        <div className="container px-6 mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading text-primary font-normal tracking-tight">System knowledge for seekers</h2>
            <div className="h-1 w-20 bg-secondary mx-auto mt-4"></div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200">
              <PolicyColumn
                title="Shipping Protocol"
                href="/shipping-policy"
                icon={<><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" /><path d="M15 18H9" /><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" /><circle cx="17" cy="18" r="2" /><circle cx="7" cy="18" r="2" /></>}
                lines={[
                  'Worldwide Shipping is available.',
                  '1. Free shipping on orders over INR 5,000 in India.',
                  '2. COD is available for orders over INR 5,000 in India.',
                  '3. International Express Shipping takes 4-7 days for delivery.',
                ]}
              />
              <PolicyColumn
                title="Return Protocol"
                href="/return-policy"
                icon={<><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 16H3v5" /></>}
                lines={[
                  '1. Get 100% moneyback on returning loose gemstones within 10 days for a full refund of the gemstone price.',
                  "2. Return Shipment is at customer's cost.",
                  '3. Shipping Charges, GST/VAT and duties are not refundable.',
                ]}
              />
              <PolicyColumn
                title="Payment System"
                href="/payment-methods"
                icon={<><rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" /></>}
                lines={[
                  '1. Credit Cards: All Visa, MasterCard and American Express Credit Cards are accepted',
                  '2. Debit Cards (India): All Visa and Maestro Debit Cards are accepted.',
                  '3. PayPal, Net Banking, Cash Cards',
                ]}
              />
            </div>
          </div>
        </div>
      </section>

      {/* SIMILAR ITEMS */}
      {similar.length > 0 && <SimilarProducts products={similar} />}

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(false)}
          className="fixed inset-0 z-300 bg-black/90 flex items-center justify-center p-6 cursor-zoom-out"
        >
          <img src={gallery[activeImage] ?? gallery[0]} alt={product.name} className="max-h-[90vh] max-w-[90vw] object-contain rounded-xl" />
          <button
            type="button"
            onClick={() => setLightbox(false)}
            className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-white/20 transition-all"
          >
            <i className="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>
      )}
    </div>
  )
}

function DetailsSkeleton() {
  return (
    <div className="flex flex-col min-h-screen bg-background select-none">
      <div className="bg-white pt-28 md:pt-32 pb-10 md:pb-14 border-b border-gray-200">
        <div className="container px-6 mx-auto animate-pulse">
          <div className="mb-6 h-3 w-64 rounded bg-gray-100" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-6 flex gap-4">
              <div className="hidden sm:flex flex-col gap-3 w-20 shrink-0">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="aspect-square rounded-lg bg-gray-200" />
                ))}
              </div>
              <div className="flex-1 aspect-square rounded-xl bg-gray-200" />
            </div>
            <div className="lg:col-span-6 space-y-6">
              <div className="h-5 w-24 rounded-lg bg-gray-200" />
              <div className="h-8 w-3/4 rounded bg-gray-200" />
              <div className="h-8 w-40 rounded bg-gray-200" />
              <div className="space-y-2">
                <div className="h-3 w-full rounded bg-gray-100" />
                <div className="h-3 w-5/6 rounded bg-gray-100" />
                <div className="h-3 w-2/3 rounded bg-gray-100" />
              </div>
              <div className="space-y-3 pt-4 border-t border-dashed border-gray-200">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-10 rounded-lg bg-gray-100" />
                ))}
              </div>
              <div className="flex gap-3 pt-4">
                <div className="h-12 flex-1 rounded-lg bg-gray-200" />
                <div className="h-12 flex-1 rounded-lg bg-gray-200" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function OptionRow({ label, options, value, onChange }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
      <label className="text-[10px] uppercase tracking-widest text-gray-500">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full sm:w-64 h-10 border border-gray-200 rounded-lg px-4 text-xs text-gray-800 outline-none focus:ring-2 focus:ring-secondary/20 normal-case"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.price > 0 ? `${o.label} (+₹${o.price.toLocaleString('en-IN')})` : o.label}
          </option>
        ))}
      </select>
    </div>
  )
}

function FeatureItem({ title, subtitle, children }) {
  return (
    <div className="flex items-start gap-4">
      <div className="mt-1 p-2 rounded-lg bg-gray-50 text-gray-400 border border-gray-100">
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">{children}</svg>
      </div>
      <div>
        <p className="text-[10px] uppercase font-bold text-gray-900 mb-0.5">{title}</p>
        <p className="text-[10px] text-gray-500 normal-case">{subtitle}</p>
      </div>
    </div>
  )
}

function PolicyColumn({ title, href, icon, lines }) {
  return (
    <div className="flex flex-col">
      <div className="bg-gray-50/50 px-8 py-4 border-b border-gray-100 flex items-center gap-3">
        <svg className="w-5 h-5 text-secondary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{icon}</svg>
        <h3 className="font-heading text-primary font-normal uppercase tracking-tight text-sm">{title}</h3>
      </div>
      <div className="p-8 flex-1 flex flex-col justify-between">
        <div className="space-y-4 text-xs text-gray-500 leading-relaxed normal-case">
          {lines.map((l, i) => <p key={i}>{l}</p>)}
        </div>
        <a href={href} className="inline-flex items-center gap-1 text-[10px] font-normal uppercase tracking-widest text-secondary hover:text-primary pt-6 transition-colors">
          Know More
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6" /></svg>
        </a>
      </div>
    </div>
  )
}
